import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Alert
} from '@mui/material';
import QuickBooksManagement from '../components/quickbooks/QuickBooksManagement';
import OTAManagement from '../components/ota/OTAManagement';
import ChannelManagerComponent from '../components/integrations/ChannelManagerComponent';
import WebhookComponent from '../components/integrations/WebhookComponent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`integrations-tabpanel-${index}`}
      aria-labelledby={`integrations-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const IntegrationsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom sx={{ p: 3, pb: 0 }}>
        Third-Party Integrations
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ px: 3, mb: 3 }}>
        Manage connections to external services and automate your workflows
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="QuickBooks" />
          <Tab label="OTA Platforms" />
          <Tab label="Channel Manager" />
          <Tab label="Webhooks" />
          <Tab label="Email Services" />
          <Tab label="SMS Services" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <QuickBooksManagement />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <OTAManagement />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ChannelManagerComponent />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <WebhookComponent />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Email service integrations are coming soon. Currently using SMTP configuration.
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Mailchimp
                  </Typography>
                  <Typography color="text.secondary">
                    Sync guest data and send automated marketing campaigns
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Status: Coming Soon
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    SendGrid
                  </Typography>
                  <Typography color="text.secondary">
                    Reliable email delivery and analytics
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Status: Coming Soon
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Box sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Twilio SMS integration is active and configured for two-way messaging.
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Twilio SMS
                  </Typography>
                  <Typography color="text.secondary">
                    Two-way SMS messaging with guests, automated responses, and conversation management
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2 }} color="success.main">
                    Status: Active ✓
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Additional SMS Providers
                  </Typography>
                  <Typography color="text.secondary">
                    Support for additional SMS providers (AWS SNS, MessageBird, etc.)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Status: Coming Soon
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default IntegrationsPage;